import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { workspaceSummary } from '../../lib/workspace-summary'
import type { Workspace } from 'shared'
import {
  Alert,
  Button,
  Card,
  EmptyState,
  PageHeader,
  SectionSpinner,
  Tabs,
  TabsList,
  Tab,
  TabsPanel,
} from '../../components/ui'
import WorkspaceMembersPanel from '../../components/workspace/WorkspaceMembersPanel'
import WorkspaceProfileForm, {
  fromWorkspace,
  toApiPayload,
  type WorkspaceProfileValues,
} from '../../components/workspace/WorkspaceProfileForm'
import { api } from '../../utils/api'
import { toErrorMessage } from '../../lib/errors'

const API = '/api/partner/workspaces'

/** One company a partner created: its people and invitations, and the profile they entered. */
export default function PartnerWorkspace() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const kickoffFailed = Boolean(
    (useLocation().state as { kickoffFailed?: boolean } | null)?.kickoffFailed
  )
  const { workspaceId } = useParams<{ workspaceId: string }>()

  const [workspace, setWorkspace] = useState<Workspace | null>(null)
  const [profile, setProfile] = useState<WorkspaceProfileValues | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    const response = await api.get<{ data: Workspace }>(`${API}/${workspaceId}`)
    setWorkspace(response.data)
    setProfile(fromWorkspace(response.data))
  }, [workspaceId])

  useEffect(() => {
    if (!workspaceId) return

    load()
      .catch((err) => setError(toErrorMessage(err)))
      .finally(() => setLoading(false))
  }, [workspaceId, load])

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!profile || !workspaceId) return

    setSaving(true)
    setError(null)
    try {
      await api.put(`${API}/${workspaceId}`, toApiPayload(profile))
      await load()
    } catch (err) {
      setError(toErrorMessage(err) ?? t('errors.generic'))
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <SectionSpinner />

  if (!workspace || !profile) {
    return (
      <EmptyState
        title={t('errors.notFound')}
        description={t('partner.notFoundDescription')}
        action={
          <Button variant="secondary" onClick={() => navigate('/partner')}>
            {t('common.back')}
          </Button>
        }
      />
    )
  }

  return (
    <>
      <PageHeader
        title={workspace.name}
        subtitle={workspaceSummary(workspace, t) || undefined}
      />

      {kickoffFailed && <Alert className="mb-6">{t('partner.kickoffFailed')}</Alert>}
      {error && <Alert className="mb-6">{error}</Alert>}

      <Tabs defaultValue="people">
        <TabsList className="mb-6 w-full">
          <Tab value="people">{t('partner.tabs.people')}</Tab>
          <Tab value="profile">{t('partner.tabs.profile')}</Tab>
        </TabsList>

        <TabsPanel value="people">
          <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
            {t('partner.peopleNote')}
          </p>
          <WorkspaceMembersPanel
            workspaceId={workspace.id}
            apiBase={API}
            canManage
            canEditMembers={false}
          />
        </TabsPanel>

        <TabsPanel value="profile">
          <Card className="p-6">
            <form onSubmit={handleSave} className="space-y-6">
              <WorkspaceProfileForm values={profile} onChange={setProfile} disabled={saving} />
              <div className="flex justify-end border-t border-gray-200 pt-6 dark:border-gray-800">
                <Button type="submit" loading={saving}>
                  {t('common.save')}
                </Button>
              </div>
            </form>
          </Card>
        </TabsPanel>
      </Tabs>
    </>
  )
}
